import { Controller } from '@hotwired/stimulus'

/**
 * Provides typeahead search for insurance payers when "Other" is selected
 * Helps families find their actual payer instead of entering it manually
 */
export default class extends Controller {
  static targets = ['select', 'textInput', 'suggestions', 'container', 'textFieldWrapper']

  static values = {
    payers: { type: Array, default: [] },
  }

  connect() {
    this.boundHandleInput = this.handleInput.bind(this)
    this.textInputTarget.addEventListener('input', this.boundHandleInput)
    this.textInputTarget.addEventListener('focus', this.boundHandleInput)

    // Close suggestions when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener('click', this.boundCloseOnClickOutside)
  }

  disconnect() {
    this.textInputTarget.removeEventListener('input', this.boundHandleInput)
    this.textInputTarget.removeEventListener('focus', this.boundHandleInput)
    document.removeEventListener('click', this.boundCloseOnClickOutside)
  }

  handleInput(event) {
    const searchTerm = event.target.value.toLowerCase().trim()

    if (searchTerm.length === 0) {
      this.hideSuggestions()
      return
    }

    // Filter payers by partial match on name
    const matches = this.payersValue.filter(([name, _value]) => {
      if (_value === 'other') return false
      return name.toLowerCase().includes(searchTerm)
    })

    if (matches.length > 0) {
      this.showSuggestions(matches)
    } else {
      this.hideSuggestions()
    }
  }

  showSuggestions(matches) {
    // Limit to top 5 matches
    const limitedMatches = matches.slice(0, 5)

    this.suggestionsTarget.innerHTML = limitedMatches
      .map(
        ([name, value]) => `
        <li class="px-4 py-2 hover:bg-sand cursor-pointer text-sm" data-value="${this.escapeHtml(
          value
        )}" data-action="click->insurance-payer-typeahead#selectSuggestion">
          ${this.escapeHtml(name)}
        </li>
      `
      )
      .join('')

    this.suggestionsTarget.classList.remove('hidden')
  }

  hideSuggestions() {
    this.suggestionsTarget.classList.add('hidden')
    this.suggestionsTarget.innerHTML = ''
  }

  selectSuggestion(event) {
    const payerValue = event.currentTarget.dataset.value

    this.selectTarget.value = payerValue

    this.textInputTarget.value = ''

    this.hideSuggestions()

    if (this.hasTextFieldWrapperTarget) {
      this.textFieldWrapperTarget.classList.add('hidden')
    }

    const changeEvent = new Event('change', { bubbles: true })
    this.selectTarget.dispatchEvent(changeEvent)
  }

  closeOnClickOutside(event) {
    if (!this.containerTarget.contains(event.target)) {
      this.hideSuggestions()
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
